import { marked, Renderer } from 'marked';
import katex from 'katex';
import { highlightCode, preloadHighlightJS, isLanguageSupported } from './highlight';

/**
 * Markdown parser using marked.js
 * Replaces custom parser with battle-tested library that handles:
 * - Nested lists
 * - GFM (GitHub Flavored Markdown)
 * - All edge cases
 */

// Custom renderer for code blocks with syntax highlighting
const createRenderer = (async_highlight: boolean): Renderer => {
  const renderer = new Renderer();

  // Store code blocks for async highlighting
  const codeBlocksToHighlight: { index: number; code: string; lang: string }[] = [];
  let codeBlockIndex = 0;

  renderer.code = ({ text, lang }) => {
    const language = lang || 'plaintext';
    const escapedCode = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');

    if (async_highlight) {
      // Store for later async highlighting
      const index = codeBlockIndex++;
      codeBlocksToHighlight.push({ index, code: text, lang: language });
      preloadHighlightJS();

      // Return placeholder that will be replaced
      return `__CODE_BLOCK_${index}__`;
    }

    // Sync mode - no highlighting, just escape
    return `<pre><code>${escapedCode}</code></pre>`;
  };

  // Open links in new tab
  renderer.link = ({ href, title, text }) => {
    const titleAttr = title ? ` title="${title}"` : '';
    return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
  };

  // Attach code blocks array for async processing
  (renderer as any)._codeBlocks = codeBlocksToHighlight;

  return renderer;
};

// Configure marked options
const baseOptions = {
  gfm: true,       // GitHub Flavored Markdown (tables, nested lists, etc.)
  breaks: true,    // Convert \n to <br> in paragraphs
};

type MathBlock = {
  placeholderMarkup: string;
  expression: string;
  displayMode: boolean;
};

const DISPLAY_MATH_BLOCK = /\\\[((?:.|\n)+?)\\\]/g;
const BRACKETED_DISPLAY_MATH_BLOCK = /(?:^|\n)\[\s*\n([\s\S]*?\\[\s\S]*?)\n\](?=\n|$)/g;
const INLINE_MATH = /\\\((.+?)\\\)/g;

function extractMathBlocks(markdown: string): { content: string; mathBlocks: MathBlock[] } {
  const mathBlocks: MathBlock[] = [];
  let placeholderIndex = 0;

  const createPlaceholder = (expression: string, displayMode: boolean): string => {
    const placeholderId = placeholderIndex++;
    const placeholderMarkup = displayMode
      ? `<div data-lizadocs-math-block="${placeholderId}"></div>`
      : `<span data-lizadocs-math-inline="${placeholderId}"></span>`;

    mathBlocks.push({
      placeholderMarkup,
      expression: expression.trim(),
      displayMode,
    });

    return placeholderMarkup;
  };

  const withDisplayMath = markdown
    .replace(DISPLAY_MATH_BLOCK, (_, expression: string) => createPlaceholder(expression, true))
    .replace(BRACKETED_DISPLAY_MATH_BLOCK, (match: string, expression: string) => {
      const leadingNewline = match.startsWith('\n') ? '\n' : '';
      return `${leadingNewline}${createPlaceholder(expression, true)}`;
    });

  const withInlineMath = withDisplayMath.replace(INLINE_MATH, (_, expression: string) =>
    createPlaceholder(expression, false)
  );

  return {
    content: withInlineMath,
    mathBlocks,
  };
}

function renderMathHtml(expression: string, displayMode: boolean): string {
  try {
    return katex.renderToString(expression, {
      displayMode,
      throwOnError: false,
      output: 'htmlAndMathml',
      strict: 'ignore',
      trust: false,
    });
  } catch (error) {
    console.error('[LizaDocs] KaTeX rendering failed, falling back to plain text:', error);
    return displayMode
      ? `<pre class="math-fallback">${escapeHtml(expression)}</pre>`
      : `<code class="math-fallback-inline">${escapeHtml(expression)}</code>`;
  }
}

function injectMathBlocks(html: string, mathBlocks: MathBlock[]): string {
  let renderedHtml = html;

  for (const block of mathBlocks) {
    const mathHtml = renderMathHtml(block.expression, block.displayMode);
    renderedHtml = renderedHtml.replace(
      block.placeholderMarkup,
      block.displayMode ? `<div class="math-block">${mathHtml}</div>` : mathHtml
    );
  }

  return renderedHtml;
}

/**
 * Parse markdown to HTML with async code highlighting.
 * For streaming, call this on each chunk and replace the content.
 */
export async function parseMarkdown(markdown: string): Promise<string> {
  try {
    const { content, mathBlocks } = extractMathBlocks(markdown);
    const renderer = createRenderer(true);
    const codeBlocks = (renderer as any)._codeBlocks as { index: number; code: string; lang: string }[];

    let html = marked.parse(content, { ...baseOptions, renderer }) as string;

    // Process code blocks with async highlighting
    for (const block of codeBlocks) {
      const highlighted = await highlightCode(block.code, block.lang);
      const langLabel = isLanguageSupported(block.lang) ? block.lang : '';

      const codeHtml = langLabel
        ? `<div class="code-header"><span>${block.lang}</span><button class="copy-button" data-code="${encodeURIComponent(block.code)}">Copy</button></div><pre><code class="hljs language-${block.lang}">${highlighted}</code></pre>`
        : `<pre><code>${highlighted}</code></pre>`;

      html = html.replace(`__CODE_BLOCK_${block.index}__`, codeHtml);
    }

    return injectMathBlocks(html, mathBlocks);
  } catch (error) {
    console.error('[LizaDocs] Markdown parsing failed, falling back to safe text:', error);
    return `<p>${escapeHtml(markdown)}</p>`;
  }
}

/**
 * Simple synchronous markdown for streaming preview.
 * Use this while streaming, then call parseMarkdown on completion.
 */
export function parseMarkdownSync(markdown: string): string {
  try {
    const { content, mathBlocks } = extractMathBlocks(markdown);
    const renderer = createRenderer(false);
    const html = marked.parse(content, { ...baseOptions, renderer }) as string;
    return injectMathBlocks(html, mathBlocks);
  } catch (error) {
    console.error('[LizaDocs] Markdown sync parsing failed, falling back to safe text:', error);
    return `<p>${escapeHtml(markdown)}</p>`;
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
